import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { WhatsAppButton } from "@/components/layout/WhatsAppButton";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { EnquiryPopup } from "@/components/shared/EnquiryPopup";
import { EnquiryPopupProvider } from "@/contexts/EnquiryPopupContext";
import Index from "./pages/Index";

// Lazy load all pages except Index for code splitting
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const Destinations = lazy(() => import("./pages/Destinations"));
const DestinationDetail = lazy(() => import("./pages/destinations/DestinationDetail"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Visa = lazy(() => import("./pages/Visa"));
const DestinationDetails = lazy(() => import("./pages/DestinationDetails"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const TestimonialsAdmin = lazy(() => import("./pages/admin/TestimonialsAdmin"));

const queryClient = new QueryClient();

/**
 * Google Analytics 4 (GA4) Integration Placeholder
 * 
 * To enable GA4 tracking:
 * 1. Add your GA4 Measurement ID (format: G-XXXXXXXXXX) 
 * 2. Install gtag.js in index.html or use react-ga4 package
 * 3. Track page views on route changes
 * 
 * Example with react-ga4:
 * import ReactGA from 'react-ga4';
 * ReactGA.initialize('G-XXXXXXXXXX');
 * 
 * On route change:
 * ReactGA.send({ hitType: "pageview", page: window.location.pathname });
 */

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <EnquiryPopupProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <Routes>
                {/* Redirect junk/test URLs to homepage */}
                <Route path="/face-serum-gxrcld" element={<Navigate to="/" replace />} />
                <Route path="/wool-sweater-lortoo" element={<Navigate to="/" replace />} />
                <Route path="/handmade-vase-slowpy" element={<Navigate to="/" replace />} />
                <Route path="/wooden-chair-mopukh" element={<Navigate to="/" replace />} />
                <Route path="/set-of-plates-cxlzwx" element={<Navigate to="/" replace />} />

                {/* Admin routes without header/footer */}
                <Route path="/admin/login" element={
                  <Suspense fallback={<PageLoader />}>
                    <AdminLogin />
                  </Suspense>
                } />
                <Route path="/admin/dashboard" element={
                  <Suspense fallback={<PageLoader />}>
                    <AdminDashboard />
                  </Suspense>
                } />
                <Route path="/admin/testimonials" element={
                  <Suspense fallback={<PageLoader />}>
                    <TestimonialsAdmin />
                  </Suspense>
                } />
                
                {/* Public routes with header/footer */}
                <Route path="/*" element={
                  <div className="flex flex-col min-h-screen">
                    <Header />
                    <main className="flex-1 pt-20 pb-16 md:pb-0">
                      <Suspense fallback={<PageLoader />}>
                        <Routes>
                          <Route path="/" element={<Index />} />
                          <Route path="/about" element={<About />} />
                          <Route path="/services" element={<Services />} />
                          <Route path="/destinations" element={<Destinations />} />
                          <Route path="/destinations/:slug" element={<DestinationDetail />} />
                          <Route path="/destination/:id" element={<DestinationDetails />} />
                          <Route path="/blog" element={<Blog />} />
                          <Route path="/blog/:slug" element={<BlogPost />} />
                          <Route path="/contact" element={<Contact />} />
                          <Route path="/faq" element={<FAQ />} />
                          <Route path="/visa" element={<Visa />} />
                          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                          <Route path="/terms" element={<Terms />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                    </main>
                    <Footer />
                    <WhatsAppButton />
                    <MobileBottomNav />
                    <EnquiryPopup />
                  </div>
                } />
              </Routes>
            </BrowserRouter>
          </EnquiryPopupProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
